import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { API } from '../../../lib/api';
import {
  cargarFuentes, destinos, fondoCabecera, secciones, varsTema,
} from '../../../lib/formularios';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { ENCABEZADO, useEditor } from './contexto';
import { Selector } from './controles';
import TarjetaEncabezado from './TarjetaEncabezado';
import TarjetaItem from './TarjetaItem';
import BarraFlotante from './BarraFlotante';

/**
 * Pestaña Preguntas: el lienzo con el tema del formulario, las secciones y sus tarjetas.
 * Aquí viven dos cosas que dependen de la posición real de las tarjetas: la barra flotante (va a la
 * altura de la activa) y arrastrar para reordenar.
 */
export default function EditorPreguntas() {
  const ed = useEditor();
  const { formulario, puedeEditar, activo, oscuro } = ed;
  const tema = formulario.tema;
  const movil = useIsMobile(900);
  const lista = useMemo(() => secciones(formulario.items), [formulario.items]);
  const tarjetas = useRef(new Map());
  const columnaRef = useRef(null);
  const [barraTop, setBarraTop] = useState(0);
  const [arrastre, setArrastreEstado] = useState(null); // {id, dy, antesDe, despuesDe}
  const arrastreRef = useRef(null);

  useEffect(() => {
    cargarFuentes(tema);
  }, [tema]);

  const registrar = useCallback((id, el) => {
    if (el) tarjetas.current.set(id, el);
    else tarjetas.current.delete(id);
  }, []);

  // La barra sigue a la tarjeta activa. La altura de lo de arriba cambia al escribir, al abrir la clave
  // de respuestas o al cargar una imagen: un ResizeObserver de la columna avisa de todo eso.
  const [medida, setMedida] = useState(0);
  useEffect(() => {
    const el = columnaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observador = new ResizeObserver(() => setMedida((n) => n + 1));
    observador.observe(el);
    return () => observador.disconnect();
  }, []);
  useLayoutEffect(() => {
    if (movil) return;
    const el = tarjetas.current.get(activo) || tarjetas.current.get(ENCABEZADO);
    if (el && el.offsetTop !== barraTop) setBarraTop(el.offsetTop);
  }, [activo, formulario, movil, barraTop, medida]);

  const setArrastre = (valor) => {
    arrastreRef.current = valor;
    setArrastreEstado(valor);
  };

  /* Arrastrar una tarjeta por su asa. Se calcula el hueco comparando el puntero con la mitad de cada
     tarjeta (sin contar la que se mueve) y se suelta como `item_move` después de la anterior. */
  const empezarArrastre = (e, id) => {
    if (!puedeEditar) return;
    e.preventDefault();
    e.stopPropagation();
    const origenY = e.clientY;
    const items = formulario.items;
    const mover = (ev) => {
      const y = ev.clientY;
      const resto = items.filter((it) => it.id !== id);
      let indice = resto.length;
      for (let i = 0; i < resto.length; i += 1) {
        const el = tarjetas.current.get(resto[i].id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (y < r.top + r.height / 2) {
          indice = i;
          break;
        }
      }
      setArrastre({
        id,
        dy: y - origenY,
        antesDe: resto[indice]?.id || null,
        despuesDe: indice > 0 ? resto[indice - 1].id : null,
      });
      // Desplazar la página al acercarse a los bordes, para poder llevar una pregunta lejos
      const contenedor = columnaRef.current?.closest('.fe-contenido');
      if (contenedor) {
        const caja = contenedor.getBoundingClientRect();
        if (y < caja.top + 60) contenedor.scrollBy(0, -14);
        else if (y > caja.bottom - 60) contenedor.scrollBy(0, 14);
      }
    };
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
      const a = arrastreRef.current;
      setArrastre(null);
      if (!a) return;
      const i = items.findIndex((it) => it.id === id);
      const anterior = i > 0 ? items[i - 1].id : null;
      if (a.despuesDe !== anterior) ed.moverItem(id, a.despuesDe);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
  };

  const cabecera = fondoCabecera(tema, API);
  const total = lista.length;

  return (
    <div className="fm-raiz fe-lienzo-raiz" style={varsTema(tema, { oscuro })}>
      <div className="fm-lienzo fe-lienzo">
        {!puedeEditar && (
          <div className="fe-aviso-lectura">
            <Lock /> Solo lectura: puedes ver este formulario, pero no editarlo.
          </div>
        )}
        <div className="fm-columna fe-columna" ref={columnaRef}>
          {cabecera && <div className="fm-cabecera-imagen" style={{ background: cabecera }} />}

          {lista.map((sec, i) => (
            <React.Fragment key={sec.id}>
              {total > 1 && <div className="fm-seccion-rotulo">Sección {i + 1} de {total}</div>}
              {i === 0
                ? <TarjetaEncabezado registrar={registrar} />
                : <TarjetaItem item={sec.item} registrar={registrar} arrastre={arrastre} onAsa={empezarArrastre} />}
              {sec.items.map((it) => (
                <TarjetaItem key={it.id} item={it} registrar={registrar} arrastre={arrastre} onAsa={empezarArrastre} />
              ))}
              {total > 1 && <DespuesDeSeccion seccion={sec} indice={i} total={total} />}
            </React.Fragment>
          ))}
          {arrastre && arrastre.antesDe === null && <div className="fe-soltar-final" />}

          {puedeEditar && !movil && <BarraFlotante top={barraTop} />}
        </div>
      </div>
      {puedeEditar && movil && <BarraFlotante movil />}
    </div>
  );
}

/** "Después de la sección N": a dónde va quien responde al terminarla (si ninguna respuesta decide otra cosa). */
function DespuesDeSeccion({ seccion, indice, total }) {
  const { formulario, setCampo, cambiarItem, puedeEditar } = useEditor();
  const valor = indice === 0 ? (formulario.despues_primera || 'siguiente') : (seccion.item?.despues || 'siguiente');
  const opciones = [
    { valor: 'siguiente', texto: indice + 1 < total ? 'Continuar a la siguiente sección' : 'Enviar formulario (última sección)' },
    ...destinos(formulario.items).map((d) => ({ valor: d.id, texto: d.nombre })),
    { valor: 'enviar', texto: 'Enviar formulario' },
  ];
  const cambiar = (v) => {
    if (indice === 0) setCampo('despues_primera', v);
    else cambiarItem(seccion.item.id, { despues: v });
  };
  return (
    <div className="fe-despues-seccion">
      <span>Después de la sección {indice + 1}</span>
      <Selector value={valor} onChange={cambiar} opciones={opciones} etiqueta={`Después de la sección ${indice + 1}`} disabled={!puedeEditar} />
    </div>
  );
}
